import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowUpRight, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { GOOGLE_REVIEWS_URL, T, useLang } from "@/lib/i18n";

const GoogleMark = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const Stars = () => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
    ))}
  </div>
);

export default function Reviews() {
  const { lang } = useLang();
  const t = T[lang].reviews;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = t.cards.length;

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(id);
  }, [paused, count]);

  useEffect(() => setIndex(0), [lang]);

  const go = (dir) => setIndex((i) => (i + dir + count) % count);
  const r = t.cards[index];

  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-24 lg:py-36 overflow-hidden">
      <img src="/images/reviews-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#16110C] via-[#16110C]/30 to-[#16110C]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-xl">
            <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
            <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
              {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
            </h2>
          </div>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl bg-[#2E2720] border border-stone-800 px-6 py-4 hover:border-[#C5A059]/40 transition-colors duration-300"
            data-testid="reviews-rating-badge"
          >
            <GoogleMark className="w-8 h-8" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-serif-display text-2xl font-semibold text-stone-100">5.0</span>
                <Stars />
              </div>
              <p className="text-xs text-stone-400 mt-0.5">{t.badgeCount}</p>
            </div>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          data-testid="review-slider"
          className="mt-14 max-w-3xl mx-auto"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="relative rounded-3xl bg-[#2E2720] border border-stone-800 p-8 sm:p-12 overflow-hidden min-h-[320px] flex flex-col">
            <Quote className="absolute top-6 right-8 w-16 h-16 text-[#C5A059]/10" />
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={`${lang}-${index}`}
                data-testid="review-slide"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="flex-1 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <Stars />
                  <GoogleMark className="w-5 h-5 opacity-80" />
                </div>
                <p className="mt-6 font-serif-display text-xl sm:text-2xl text-stone-200 leading-relaxed flex-1">“{r.text}”</p>
                {r.translation && (
                  <p className="mt-4 border-l-2 border-[#C5A059]/50 pl-4 text-[13px] text-stone-500 leading-relaxed italic" data-testid="review-translation">
                    {r.translation}
                    <span className="block mt-1.5 not-italic text-[10px] uppercase tracking-[0.14em] text-stone-600">
                      {lang === "de" ? (r.from === "en" ? "Aus dem Englischen übersetzt" : "Aus dem Französischen übersetzt") : "Translated from French"}
                    </span>
                  </p>
                )}
                <footer className="mt-8 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center font-serif-display font-semibold text-lg text-[#C5A059]">
                    {r.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-stone-100">{r.name}</p>
                    <p className="text-xs text-stone-500">{r.meta}</p>
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-center gap-5">
            <button
              data-testid="review-prev"
              onClick={() => go(-1)}
              aria-label="Previous review"
              className="w-10 h-10 rounded-full border border-stone-700 flex items-center justify-center text-stone-400 hover:border-[#C5A059]/60 hover:text-[#C5A059] transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-2.5" data-testid="review-dots">
              {t.cards.map((_, i) => (
                <button
                  key={i}
                  data-testid={`review-dot-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to review ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-400 ${i === index ? "w-7 bg-[#C5A059]" : "w-2 bg-stone-700 hover:bg-stone-500"}`}
                />
              ))}
            </div>
            <button
              data-testid="review-next"
              onClick={() => go(1)}
              aria-label="Next review"
              className="w-10 h-10 rounded-full border border-stone-700 flex items-center justify-center text-stone-400 hover:border-[#C5A059]/60 hover:text-[#C5A059] transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="mt-10 text-center"
        >
          <a
            data-testid="reviews-google-link"
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#C5A059]/50 px-6 py-3.5 text-sm font-semibold text-stone-100 hover:bg-[#C5A059] hover:text-[#141312] transition-colors duration-300"
          >
            <GoogleMark className="w-4 h-4" />
            {t.ctaLink}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
